import { useEffect, useMemo, useState } from "react";
import Container from "@mui/material/Container";
import Stack from "@mui/material/Stack";
import FormControl from "@mui/material/FormControl";
import Select from "@mui/material/Select";
import MenuItem from "@mui/material/MenuItem";
import Footer from "../../components/Footer";
import Description from "../../components/Description";
import Links from "../../components/Links";
import Address from "../../components/Address";
import Catalog from "../../components/Catalog";
import Loading from "../Loading";
import { Principal as TPrincipal, Address as TAddress } from "../../types/principal";
import { withinTheTimeLimit, formatDate } from "../../utils/date";
import Typography from "@mui/material/Typography";
import posthog from "posthog-js";

function normalizeAddresses(address: TPrincipal["address"]): TAddress[] {
  if (!address) return [];
  if (Array.isArray(address)) return address;
  if (typeof address === "object" && "latitude" in address && "longitude" in address)
    return [address as TAddress];
  return [];
}

type PrincipalProps = {
  data: TPrincipal;
};

const Principal = ({ data }: PrincipalProps) => {
  const [selectedAddressIndex, setSelectedAddressIndex] = useState(0);

  const addresses = useMemo(
    () => (data ? normalizeAddresses(data.address) : []),
    [data, data?.address]
  );

  if (!Boolean(data)) {
    return <Loading />;
  }

  const today = new Date();
  const catalogItens = useMemo(() => {
    return data.catalog?.items
      ?.filter((item) => withinTheTimeLimit(item, today))
      .map((item) => ({
        ...item,
        endDate: item?.endDate ? formatDate(item?.endDate) : "",
        startDate: item?.startDate ? formatDate(item?.startDate) : "",
      }));
  }, [data.catalog?.items]);

  useEffect(() => {
    const search = window.location.search || window.location.hash.split('?')[1] || '';
    const params = new URLSearchParams(search);
    const utms: { [key: string]: string } = {};

    for (const [key, value] of params.entries()) {
      if (key.startsWith('utm_')) {
        utms[key] = value;
      }
    }
    posthog.capture("Pageview", {
      store_id: data.store.id,
      store_name: data.description.title,
      company: location.pathname.split("/")[1],
      ...utms
    });
  }, [data]);

  return (
    <Stack
      minHeight="100vh"
      flex={1}
      rowGap={5}
      bgcolor={data.general.backgroundColor}
    >
      <Container maxWidth="sm" sx={{ flex: 1 }}>
        <Stack rowGap={5}>
          <Description
            icon={data.store.image}
            subtitle={data.description.subtitle}
            title={data.description.title}
          />
          <Catalog
            title={data.catalog.title}
            items={catalogItens}
            labelButton={data.catalog.labelButton}
            whatsapp={data.footer.whatsapp}
          />
          <Links
            items={data.links.items}
            title={data.links.title}
            whatsapp={data.footer.whatsapp}
          />
          {addresses.length > 0 && (() => {
            const safeIndex = Math.min(selectedAddressIndex, Math.max(0, addresses.length - 1));
            const current = addresses[safeIndex];
            return (
              <Stack rowGap={2}>
                {addresses.length > 1 && (
                  <>
                    <Typography color="primary" variant="h6" fontWeight={500}>
                      Endereço
                    </Typography>
                    <FormControl
                      size="small"
                      fullWidth
                      color="secondary"
                      sx={{
                        "& .MuiOutlinedInput-root": {
                          bgcolor: "#FFF",
                          "& fieldset": {
                            border: "none",
                          },
                          "&:hover fieldset": {
                            border: "none",
                          },
                          "&.Mui-focused fieldset": {
                            border: "none",
                          },
                        },
                      }}
                    >
                      <Select
                        labelId="address-select-label"
                        value={safeIndex}
                        color="secondary"
                        onChange={(e) =>
                          setSelectedAddressIndex(Number(e.target.value))
                        }
                      >
                        {addresses.map((addr, i) => (
                          <MenuItem key={i} value={i}>
                            <Typography color="secondary" variant="button">
                              {addr.title || `Endereço ${i + 1}`}
                            </Typography>
                          </MenuItem>
                        ))}
                      </Select>
                    </FormControl>
                  </>
                )}
                <Address
                  key={safeIndex}
                  latitude={current?.latitude ?? 0}
                  longitude={current?.longitude ?? 0}
                  title={current?.title}
                />
              </Stack>
            );
          })()}
        </Stack>
      </Container>
      <Footer data={data.footer} bgcolor={data.general.backgroundColor} />
    </Stack>
  );
};

export default Principal;
